// biome-ignore lint/suspicious/noExplicitAny: it's ok
type IsObject<T> = T extends object ? (T extends any[] ? false : true) : false;

// Main deep merge function with generics
export function deepMerge<T, U>(target: T, source: U): T & U {
  // Handle case where either is null/undefined
  if (source === null || source === undefined) {
    // biome-ignore lint/suspicious/noExplicitAny: it's ok
    return target as any;
  }

  if (target === null || target === undefined) {
    // biome-ignore lint/suspicious/noExplicitAny: it's ok
    return source as any;
  }

  // Handle arrays - concatenate and deep merge array contents
  if (Array.isArray(source)) {
    if (Array.isArray(target)) {
      // Create a copy of the target array
      // biome-ignore lint/suspicious/noExplicitAny: it's ok
      const result = [...target] as any[];

      // Add all source items to the result array
      // For source items that are objects or arrays, deep clone them
      for (const sourceItem of source) {
        if (isObject(sourceItem)) {
          result.push(deepMerge({}, sourceItem));
        } else if (Array.isArray(sourceItem)) {
          result.push(deepMerge([], sourceItem));
        } else {
          result.push(sourceItem);
        }
      }

      // biome-ignore lint/suspicious/noExplicitAny: it's ok
      return result as any;
    }

    // If target is not an array but source is, return a deep copy of source
    // biome-ignore lint/suspicious/noExplicitAny: it's ok
    return deepMerge([], source) as any;
  }

  // Handle objects (but not arrays)
  if (isObject(source)) {
    if (isObject(target)) {
      // biome-ignore lint/suspicious/noExplicitAny: it's ok
      const result = { ...target } as any;

      // Process each property in source
      for (const key in source) {
        if (Object.hasOwn(source, key)) {
          // biome-ignore lint/suspicious/noExplicitAny: it's ok
          const sourceValue = (source as any)[key];
          // biome-ignore lint/suspicious/noExplicitAny: it's ok
          const targetValue = (target as any)[key];

          // Recursively merge objects and arrays
          if (isObject(sourceValue) || Array.isArray(sourceValue)) {
            if (targetValue === undefined || targetValue === null) {
              // Create appropriate empty container if target doesn't have this key
              result[key] = deepMerge(
                Array.isArray(sourceValue) ? [] : {},
                sourceValue,
              );
            } else {
              // Otherwise merge with existing value
              result[key] = deepMerge(targetValue, sourceValue);
            }
          } else {
            // For primitive values, just assign the source value
            result[key] = sourceValue;
          }
        }
      }

      // biome-ignore lint/suspicious/noExplicitAny: it's ok
      return result as any;
    }

    // If target is not an object, return a deep copy of source
    // biome-ignore lint/suspicious/noExplicitAny: it's ok
    return deepMerge({}, source) as any;
  }

  // Default: return source for primitive values
  // biome-ignore lint/suspicious/noExplicitAny: it's ok
  return source as any;
}

// Helper function to check if something is a plain object
// biome-ignore lint/suspicious/noExplicitAny: it's ok
function isObject(item: any): boolean {
  return item && typeof item === "object" && !Array.isArray(item);
}
